import { ChangeDetectionStrategy, Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { Workspace } from '@telespot/sdk';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { WorkspaceDetailFacade } from './workspace-detail.facade';
import { workspaceDetailFacadeProviders } from './workspace-detail.facade.providers';
import { AuthService, TelespotModule } from '@telespot/web-core';
import { WorkspaceDetailData } from '../../../stores/workspace-detail.state';

@Component({
  selector: 'ts-workspace-detail',
  templateUrl: './workspace-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatTabsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslateModule,
    TelespotModule,
  ],
  providers: [...workspaceDetailFacadeProviders()]
})
export class WorkspaceDetailComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly deleteWorkspaceTrigger$ = new Subject<void>();

  public readonly data$: Observable<WorkspaceDetailData | undefined> = this.facade.workspace$;

  /**
   * Parse SDK Workspace from the route resolver. This component does not bind to it; it is
   * exposed only because the parent <ts-workspace-detail-page> still depends on Parse-only
   * relations (organization.license.features, caseType.methodTypes) for the export and
   * acquisition flows. New consumers should bind to the facade observables (data$, numCases$,
   * numMembers$, caseTypeName$) instead.
   */
  public readonly workspace$: Observable<Workspace> = this.route.data.pipe(
    map((data) => data['workspace']),
    filter((workspace): workspace is Workspace => workspace !== undefined)
  );

  constructor(
    private readonly route: ActivatedRoute,
    private readonly facade: WorkspaceDetailFacade,
    private readonly authService: AuthService
  ) {}

  // Managers/admin manage members; everyone else only views them.
  public get canManageMembers(): boolean {
    return this.authService.hasRoles(['admin', 'analystmanager', 'technicianmanager']);
  }

  public get numCases$(): Observable<number> {
    return this.facade.numCases$;
  }

  public get numMembers$(): Observable<number> {
    return this.facade.numMembers$;
  }

  public get caseTypeName$(): Observable<string> {
    return this.facade.caseTypeName$;
  }

  public get canBeDeleted$(): Observable<boolean> {
    return this.facade.canBeDeleted$;
  }

  public get deleting$(): Observable<boolean> {
    return this.facade.deletingWorkspace$;
  }

  public get deletionError$(): Observable<unknown> {
    return this.facade.workspaceDeletionError$;
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((p) => p.get('workspaceId')),
        filter((id): id is string => !!id),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((workspaceId) => this.facade.initialize(workspaceId));
    this.facade.deleteWorkspace(this.deleteWorkspaceTrigger$);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.deleteWorkspaceTrigger$.complete();
  }

  public deleteWorkspace(): void {
    this.deleteWorkspaceTrigger$.next();
  }
}
